// Module 11 — uPlot ring-buffer wrapper (one per ChartDef). A LiveChart owns the time-series buffer
// for its chart: each new snapshot pushes (tick, projected y-values) and pushes ONE setData to uPlot.
// MAX_POINTS caps retained points so layout never thrashes (dashboards.v1 invariant). Written against
// the structural UPlotCtor/UiElement seam so it is testable in node with a recording fake.
import type { Snapshot } from "../../domain/index";
import type { ChartDef } from "../series";
import type { UPlotCtor, UPlotLike, UiElement, AlignedData } from "../seam";

// seaborn-colorblind: a CVD-safe series palette that reads clearly on the dark HUD card and shares the
// map's Okabe-Ito vocabulary (Batch C 4.4). uPlot's DEFAULT stroke is BLACK 1px (invisible on dark) —
// these are assigned per series index within each chart; the always-on totals header carries live values.
const SERIES_COLORS = ["#0173b2", "#de8f05", "#029e73", "#cc78bc", "#ca9161", "#949494"] as const;

export const MAX_POINTS = 240; // caps retained points so layout never thrashes (dashboards.v1 invariant)

export interface LiveChart {
  key: string;
  redraw(): void; // push the caller-owned buffer to uPlot
  destroy(): void;
}

// Pure sampler — appends one projected sample to a CALLER-OWNED ring buffer. The panel calls this EVERY
// tick (even while the chart panel is closed) so history is continuous; the uPlot is built lazily on
// first open and seeded with the whole buffer, so a freshly-opened chart shows its full history at once
// (no more blank charts). MAX_POINTS caps retained points so layout never thrashes (dashboards.v1).
export function pushChartSample(def: ChartDef, snap: Snapshot, xs: number[], ys: number[][]): void {
  const values = def.projectSnap ? def.projectSnap(snap) : def.project ? def.project(snap.totals) : [];
  xs.push(snap.tick);
  for (let i = 0; i < ys.length; i++) ys[i]!.push(values[i] ?? 0);
  if (xs.length > MAX_POINTS) {
    xs.shift();
    for (const col of ys) col.shift();
  }
}

// Build a uPlot for a chart, SEEDED with the caller's current buffer (xs/ys). Sizes to the host so it
// never overflows a narrow (phone) panel — charts build on first OPEN, so the host is visible + measured.
export function makeLiveChart(uplot: UPlotCtor, target: UiElement, def: ChartDef, xs: number[], ys: number[][]): LiveChart {
  const data = (): AlignedData => [xs, ...ys];
  const HEIGHT = 150;
  // size to the host panel's content width. The `.chart` div starts at clientWidth 0 (before uPlot creates
  // its canvas), so we measure the PARENT panel instead — it's the stable layout container. The parent is
  // the chart-panel body which has a fixed CSS width from the panel dock. Fall back to a panel-fitting 336.
  const measure = (): number => {
    const self = (target as { clientWidth?: number }).clientWidth;
    if (self && self > 0) return Math.max(240, Math.min(460, self));
    // target not yet reflowed — measure the parent panel instead (the stable container)
    const parent = (target as { parentElement?: { clientWidth?: number } }).parentElement;
    const pw = parent?.clientWidth;
    return pw && pw > 0 ? Math.max(240, Math.min(460, pw)) : 336;
  };
  let width = measure();
  const u: UPlotLike = new uplot(
    {
      // a subtitle (e.g. "60.00 Hz nominal") rides in the uPlot title so a steady SD line reads as healthy
      title: def.subtitle ? `${def.title} · ${def.subtitle}` : def.title,
      width,
      height: HEIGHT,
      series: [
        { label: "tick" },
        ...def.seriesLabels.map((label, i) => ({
          label,
          stroke: SERIES_COLORS[i % SERIES_COLORS.length]!,
          width: 2,
        })),
      ],
      scales: { x: { time: false } }, // x = tick index, not Unix time (no 1969 dates)
      axes: [
        { stroke: "#8a93a0", grid: { stroke: "rgba(255,255,255,0.07)" }, ticks: { stroke: "rgba(255,255,255,0.15)" }, font: "10px system-ui" },
        { stroke: "#8a93a0", grid: { stroke: "rgba(255,255,255,0.07)" }, ticks: { stroke: "rgba(255,255,255,0.15)" }, font: "10px system-ui", label: def.unit ?? "MW", labelSize: 22, labelFont: "11px system-ui" },
      ],
    },
    data(),
    target,
  );

  // BUG D2 FIX: ResizeObserver — when the host panel reflows (open/close/resize), re-measure + setSize so
  // the canvas doesn't stay stuck at the 300×150 HTML default. Guarded for the test fake (no ResizeObserver).
  let resizeObs: { observe(el: Element): void; disconnect(): void } | null = null;
  if (typeof ResizeObserver !== "undefined") {
    resizeObs = new ResizeObserver(() => {
      const w = measure();
      if (w !== width && u.setSize) { width = w; u.setSize({ width, height: HEIGHT }); }
    });
    resizeObs.observe(target as unknown as Element);
  }

  return {
    key: def.key,
    redraw() {
      const w = measure();
      if (w !== width && u.setSize) { width = w; u.setSize({ width, height: HEIGHT }); } // auto-fit to the panel
      u.setData(data());
      // BUG D FIX: uPlot's legend table only shows values while the cursor hovers the plot (idle = "--").
      // Synthesize a cursor position at the LAST data point so the legend always shows the latest values
      // without requiring user interaction. The real uPlot exposes setCursor; the seam's UPlotLike doesn't
      // (it's optional), so cast to the real shape. Guarded so the test fake (no setCursor) is unaffected.
      const realU = u as UPlotLike & { setCursor?(opts: { left: number; top: number }): void; width?: number };
      if (realU.setCursor && typeof realU.width === "number" && realU.width > 0 && xs.length > 0) {
        // map the last x value to a pixel position: uPlot's cursor uses device pixels relative to the plot area
        const lastX = xs[xs.length - 1]!;
        const firstX = xs[0] ?? lastX;
        const range = Math.max(1, lastX - firstX);
        const plotWidth = Math.max(0, realU.width - 50); // approximate plot area (minus left padding/axis)
        const left = Math.min(plotWidth, Math.max(0, ((lastX - firstX) / range) * plotWidth));
        realU.setCursor({ left, top: 0 });
      }
    },
    destroy() {
      resizeObs?.disconnect();
      u.destroy();
    },
  };
}
